<template>
  <div>
    <Confirm
      :title="$t('COMMON.CONFIRM')"
      :message="$t('EVENT.CONFIRM_DELETE')"
      ref="delete-event-confirm"
      type="success"
      @confirm="on_delete_event_confirm"
    />
    <ErrorPopup
      :error="error_popup_message"
    />

    <m-row class="align-items-center mb-4 mt-8">
      <m-col sm="6">
        <memlist-checkbox 
          :text="$t('EVENT.PREVIOUS_EVENTS')" 
          v-model="show_history" 
        />
      </m-col>

      <m-col sm="6" class="d-flex justify-end">
        <memlist-icon-button
          icon="menu-icon flaticon2-plus"
          :text="$t('EVENT.NEW_EVENT')"
          variant="primary"
          @click="create_event_clicked"
        />
      </m-col>
    </m-row>


    <memlist-static-table
      id="events-table"
      style="width: 100%; table-layout: fixed;"
      class="table-striped"
      :fields="headers"
      :items="items"
      head-variant="light"
      ref="event-stable"
    >
      <template #cell(status)="row">

        <memlist-badge
          v-if="row.item.status === 'UNKNOWN'"
          status="danger"
        >
        {{ $t('EVENT.STATUSES.UNKNOWN') }}
        </memlist-badge>

        <memlist-badge
          v-if="row.item.status === 'ARCHIVED'"
          status="secondary"
        >
        {{ $t('EVENT.STATUSES.ARCHIVED') }}
        </memlist-badge>

        <memlist-badge
          v-if="row.item.status === 'DRAFT'"
          status="primary"
        >
        {{ $t('EVENT.STATUSES.DRAFT') }}
        </memlist-badge>

        <memlist-badge
          v-if="row.item.status === 'ACTIVE'"
          status="success"
        >
        {{ $t('EVENT.STATUSES.ACTIVE') }}
        </memlist-badge>

      </template>

      <template #cell(event_id)="row">
        <div class='justify-content-end d-flex'>

          <memlist-table-button class="mr-3" @click="goto_event_url(row.item.event_id)" svg="/assets/svg/Directions.svg" :tooltip="$t('EVENT.TOOLTIP_URL')" />
          
          <memlist-table-button class="mr-3" @click="select_event_clicked(row.item.event_id)" svg="/assets/svg/pencil-square.svg" :tooltip="$t('EVENT.TOOLTIP_EDIT')" />
          
          
          <memlist-table-button class="mr-3" @click="copy_event_clicked(row.item.event_id)" icon="far fa-copy" :tooltip="$t('EVENT.TOOLTIP_COPY')" />
          
          <memlist-table-button v-if="row.item.status === 'ARCHIVED'" class="mr-3" @click="delete_event_clicked(row.item.event_id)" svg="/assets/svg/trash2.svg" :tooltip="$t('EVENT.TOOLTIP_DELETE')" />
          <memlist-table-button v-if="row.item.status === 'DRAFT' || row.item.status === 'ACTIVE'" class="mr-3" @click="archive_event_clicked(row.item)" icon="fa fa-archive" :tooltip="$t('EVENT.TOOLTIP_ARCHIVE')" />
          
        </div>
      </template>

    </memlist-static-table>
    
  </div>
  
</template>

<script>
import axios from 'axios';
import dayjs from 'dayjs';
import { toasts } from '@/core/mixins/toastr-helper.mixin.js';
import { mapGetters } from 'vuex';
import ErrorPopup from '@/view/components/ErrorPopup.vue';

export default {
  name: 'EventsTable',
  mixins: [ toasts ],
  emits: ['create_event_clicked', 'select_event_clicked', 'publish_event_clicked'],
  components: {
    ErrorPopup
  },
  computed: {
    ...mapGetters(['companies', 'currentCompanyId', 'currentPeriodId']),
  },

  watch: {
    show_history(newcheck, oldcheck) {
      this.load_events();
    }
  },
  methods: {
    refresh() {
      this.load_events();
    },
    async archive_event_clicked(event) {
      await axios.put(`/events/${event.event_id}`, { status: 'ARCHIVED' });
      this.load_events();
    },
    create_event_clicked() {
      this.$emit('create_event_clicked');
    },
    select_event_clicked(event_id) {
      this.$emit('select_event_clicked', event_id);
    },
    
    async copy_event_clicked(event_id) {
      const res = await axios.post(`/events/${event_id}/copy`);

      if (res.status === 201) {
        this.toastr('success', this.$t('COMMON.OK'), this.$t('EVENT.COPIED_EVENT'));
        this.load_events();
      }
      
    },

    delete_event_clicked(event_id) {
      this.to_delete_id = event_id;
      this.$refs['delete-event-confirm'].show();
    },
    on_delete_event_confirm() {
      this.delete_event(this.to_delete_id);
      this.to_delete_id = null;
    },
    async delete_event(event_id) {
      try {
        const res = await axios.delete(`/events/${event_id}&company_id=${this.currentCompanyId}`);

        if (res.status === 204) {
          this.events = this.events.filter(item => item.event_id !== event_id);
        }
        else if (res.status === 403) {
          this.error_popup_message = 'ERR_FORBIDDEN';

          this.$nextTick(()=>{ this.error_popup_message = null; });
          return;
        }
        else if (res.status === 409) {
          this.toastr(
            'danger',
            this.$t('COMMON.ERROR'),
            this.$t('EVENT.UNABLE_DELETE_CONFLICT')
          );
        } else {
          this.toastr('danger', this.$t('COMMON.ERROR'), this.$t('EVENT.UNABLE_DELETE'));
        }
      }
      catch (err) {
        console.error('delete_event error', err);
        this.toastr('danger', this.$t('COMMON.ERROR'), this.$t('EVENT.UNABLE_DELETE'));
      }
      
    },

    publish_event_clicked(event_id){
      this.$emit('publish_event_clicked', event_id)
    },
    gotoEvent(event_id){
      this.$router.push(`/event/${event_id}`)
    },
    goto_event_url(event_id) {
      window.open('/event/'+event_id, '_blank');
    },
    async load_events() {
      
      const loader = this.$loading.show();
      this.items = [];

      try {
        const res = await axios.get(`/events/list?company_id=${this.currentCompanyId}`);
        if (res.status === 200) {
          let events = res.data;


          if (res.status === 403) {
            this.error_popup_message = 'ERR_FORBIDDEN';

            this.$nextTick(()=>{ this.error_popup_message = null; });
            return;
          }

          for (const event of events) {
            //var eventdt = dayjs(event.to_datetime);
            if (!this.show_history && event.status !== 'DRAFT' && event.status !== 'ACTIVE') {
              continue;
            }
            this.items.push(event);
          }
        } else {
          console.error(res);
          this.toastr('danger', this.$t('COMMON.ERROR'), this.$t('EVENT.UNABLE_LIST'));
        }
      } catch (err) {
        console.error('load_events error', err);
        this.toastr('danger', this.$t('COMMON.ERROR'), this.$t('EVENT.UNABLE_LIST'));
      } finally {
        loader && loader.hide();
      }
    },
  },
  mounted() {
    this.load_events();
  },
  data() {
    return {
      error_popup_message: null,
      to_delete_id: null,
      items: [],
      textFilter: null,
      show_history: false,
      headers: [
        {
          key: 'name',
          label: this.$t('EVENT.EVENTS'),
          sortable: true,
          thClass: 'td-overflow',
          tdClass: 'td-overflow'
        },
        {
          key: 'status',
          label: this.$t('EVENT.STATUS'),
          sortable: true,
          thClass: 'w-140px align-middle',
          tdClass: 'w-140px',
        },
        {
          key: 'from_datetime',
          label: this.$t('EVENT.START_TIME'),
          sortable: true,
          thClass: 'w-140px align-middle',
          tdClass: 'w-140px',
          formatter: (_, __, item) => {
            return dayjs(item.from_datetime).isValid() ? dayjs(item.from_datetime).format('YYYY-MM-DD') : '-';
          },
          
        },
        {
          key: 'signups',
          label: this.$t('EVENT.NUM_SIGNUPS'),
          formatter: (_, __, item) => {
            return item.num_tickets ? item.num_tickets : 0;
          },
          sortByFormatted: true,
          sortable: true,
          thClass: 'w-100px align-middle',
          tdClass: 'w-100px',
        },
        {
          key: 'num_seats',
          label: this.$t('EVENT.NUM_SEATS'),
          sortable: true,
          thClass: 'w-150px align-middle',
          tdClass: 'w-150px',
        },
        {
          key: 'event_id',
          label: this.$t('COMMON.ACTIONS'),
          thClass: 'text-right',
        },
      ],
      list: []
    };
  }
};
</script>
